import React, { useState } from 'react';
import { CalendarIcon, ClockIcon, SendIcon } from 'lucide-react';
interface ScheduleVisitFormProps {
  listingId: string;
  onSubmit: (formData: {
    name: string;
    email: string;
    phone: string;
    date: string;
    time: string;
    message: string;
  }) => void;
}
const ScheduleVisitForm: React.FC<ScheduleVisitFormProps> = ({
  listingId,
  onSubmit
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    message: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.time) newErrors.time = 'Time is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };
  const timeSlots = [];
  for (let hour = 9; hour <= 17; hour++) {
    const hourStr = hour <= 12 ? hour : hour - 12;
    const period = hour < 12 ? 'AM' : 'PM';
    timeSlots.push(`${hourStr}:00 ${period}`);
    timeSlots.push(`${hourStr}:30 ${period}`);
  }
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];
  return <div className="bg-white rounded-lg shadow-md p-6" data-id="element-257">
      <h3 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-258">
        Schedule a Visit
      </h3>
      <form onSubmit={handleSubmit} data-id="element-259">
        <div className="space-y-4" data-id="element-260">
          <div data-id="element-261">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-262">
              Full Name
            </label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={`w-full px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`} data-id="element-263" />
            {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-264">{errors.name}</p>}
          </div>
          <div data-id="element-265">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-266">
              Email
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`w-full px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`} data-id="element-267" />
            {errors.email && <p className="mt-1 text-sm text-red-600" data-id="element-268">{errors.email}</p>}
          </div>
          <div data-id="element-269">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-270">
              Phone Number
            </label>
            <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={`w-full px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${errors.phone ? 'border-red-500' : 'border-gray-300'}`} data-id="element-271" />
            {errors.phone && <p className="mt-1 text-sm text-red-600" data-id="element-272">{errors.phone}</p>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-273">
            <div data-id="element-274">
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-275">
                Preferred Date
              </label>
              <div className="relative" data-id="element-276">
                <input type="date" id="date" name="date" min={minDate} value={formData.date} onChange={handleChange} className={`w-full px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 pl-10 ${errors.date ? 'border-red-500' : 'border-gray-300'}`} data-id="element-277" />
                <CalendarIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-278" />
              </div>
              {errors.date && <p className="mt-1 text-sm text-red-600" data-id="element-279">{errors.date}</p>}
            </div>
            <div data-id="element-280">
              <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-281">
                Preferred Time
              </label>
              <div className="relative" data-id="element-282">
                <select id="time" name="time" value={formData.time} onChange={handleChange} className={`w-full px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 pl-10 appearance-none ${errors.time ? 'border-red-500' : 'border-gray-300'}`} data-id="element-283">
                  <option value="" data-id="element-284">Select a time</option>
                  {timeSlots.map(time => <option key={time} value={time} data-id="element-285">
                      {time}
                    </option>)}
                </select>
                <ClockIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-286" />
              </div>
              {errors.time && <p className="mt-1 text-sm text-red-600" data-id="element-287">{errors.time}</p>}
            </div>
          </div>
          <div data-id="element-288">
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-289">
              Message (Optional)
            </label>
            <textarea id="message" name="message" rows={3} value={formData.message} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Any specific questions or requests?" data-id="element-290" />
          </div>
          <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-md transition duration-200 flex items-center justify-center" data-id="element-291">
            <SendIcon className="h-5 w-5 mr-2" data-id="element-292" />
            Request Visit
          </button>
        </div>
      </form>
    </div>;
};
export default ScheduleVisitForm;